<template>
  <Dialog :open="open" @update:open="$emit('update:open', $event)">
    <DialogContent class="sm:max-w-[450px]">
      <DialogHeader>
        <DialogTitle>{{ $t("feedback.title") }}</DialogTitle>
      </DialogHeader>

      <div class="flex flex-row items-center justify-center gap-2 my-4">
        <div
          v-for="i in 5"
          :key="i"
          class="w-8 h-8 cursor-pointer"
          @click="handleStarClick(i)"
        >
          <Star v-if="feedbackRating < i" />
          <div v-else>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="currentColor"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-star-icon lucide-star"
            >
              <path
                d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"
              />
            </svg>
          </div>
        </div>
      </div>

      <form @submit.prevent="onSubmit">
        <FormField v-slot="{ componentField }" name="content">
          <FormItem>
            <FormControl>
              <Textarea
                v-bind="componentField"
                :placeholder="$t('feedback.description')"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
        <div class="flex justify-end">
          <Button
            class="flex mt-4 rounded-[8px]"
            type="submit"
            variant="light"
            size="lg"
          >
            {{ $t("common.submit") }}
          </Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Star } from "lucide-vue-next";

import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";

import { toTypedSchema } from "@vee-validate/zod";
import * as z from "zod";
import { useForm } from "vee-validate";

defineProps({
  open: {
    type: Boolean,
    required: true,
  },
});

const { t } = useI18n();

const formSchema = toTypedSchema(
  z.object({
    content: z.string().max(5000).optional(),
  }),
);

const { handleSubmit } = useForm({
  validationSchema: formSchema,
});

const emit = defineEmits(["update:open"]);
const feedbackRating = ref(3);

const handleStarClick = (index: number) => {
  feedbackRating.value = index;
};

const onSubmit = handleSubmit(async (values) => {
  try {
    const feedback = await $fetch("/api/feedbacks", {
      method: "POST",
      body: {
        rating: feedbackRating.value,
        content: values.content,
      },
    });

    if (!feedback) throw new Error("Failed to create feedback");

    create({
      title: t("feedback.success"),
      type: "info",
    });

    emit("update:open", false);
  } catch {
    create({
      title: t("feedback.error"),
      type: "error",
    });
  }
});
</script>
